import React, { useMemo } from 'react'
import styled, { keyframes } from 'styled-components'
import { ink, surface, line, radius, font, bp, cardStyle, transition } from 'features/dashboard/tokens'
import ComplianceScoreExplainer from 'features/compliance/ComplianceScoreExplainer'
import { RAG, scoreBandFor } from 'features/dashboard/charts/scoreColour'

const bandFor = score => {
  const band = scoreBandFor(score)
  if (!band) return { label: 'Setting up', tint: ink.mute, accent: ink.mute }
  const colour = RAG[band.level].fg
  return { label: band.label, tint: colour, accent: colour }
}

const formatScore = score => score === null || score === undefined ? 'No score' : `${Math.round(score)}`

const ComplianceHeadline = ({
  scopeEyebrow,
  scopeLabel,
  score,
  scoreExplain,
  trendDelta,
  trendHint,
  trendExplain,
  subline,
  metrics = [],
  scoreTask,
  scoreAction,
  scoreRisk,
  scoredCount,
  totalCount,
}) => {
  const partial = Number.isFinite(scoredCount) && Number.isFinite(totalCount) && totalCount > 0 && scoredCount < totalCount
  const mostlyUnscored = partial && (scoredCount / totalCount) < 0.5
  const band = useMemo(() => {
    if (mostlyUnscored) return { label: 'Setup in progress', tint: RAG.medium.fg, accent: RAG.medium.fg }
    return bandFor(score)
  }, [score, mostlyUnscored])
  const showSplit = [scoreTask, scoreAction, scoreRisk].some(v => v !== null && v !== undefined)
  const trendArrow = trendDelta === null || trendDelta === undefined
    ? null
    : trendDelta > 0 ? '▲' : trendDelta < 0 ? '▼' : '◆'

  return (
    <Card $accent={band.accent}>
      <AccentRail $accent={band.accent} />
      <MainRow>
        <Left>
          <Eyebrow>
            {scopeEyebrow || 'Compliance now'}
            {scopeLabel && <ScopeChip>· {scopeLabel}</ScopeChip>}
          </Eyebrow>
          <ScoreRow>
            <ScoreValue $tint={band.tint} title={scoreExplain || undefined}>{formatScore(score)}</ScoreValue>
            {score != null && <ScoreUnit>%</ScoreUnit>}
            <BandChip $tint={band.tint}>{band.label}</BandChip>
            {partial && (
              <Provisional title={`This score covers only the ${scoredCount} of ${totalCount} Building Passports that are set up enough to be scored. The other ${totalCount - scoredCount} are still in setup and are not yet assessed, so this is not a full picture.`}>
                Provisional <Info aria-hidden='true'>ⓘ</Info>
              </Provisional>
            )}
            <ComplianceScoreExplainer align='left' />
            {trendArrow && (
              <Trend
                $tone={trendDelta > 0 ? 'up' : trendDelta < 0 ? 'down' : 'flat'}
                title={trendExplain || undefined}
              >
                {trendArrow} {Math.abs(trendDelta)}{' '}
                <TrendHint>{trendHint || 'vs last period'}</TrendHint>
                {trendExplain && <Info aria-hidden='true'>ⓘ</Info>}
              </Trend>
            )}
          </ScoreRow>
          {metrics.length > 0 ? (
            <MetricRow>
              {metrics.map(m => (
                <Metric
                  key={m.key}
                  as={m.onClick ? 'button' : 'div'}
                  type={m.onClick ? 'button' : undefined}
                  $clickable={!!m.onClick}
                  $tone={m.tone}
                  onClick={m.onClick}
                  title={m.explain || undefined}
                >
                  <MetricValue $tone={m.tone}>{m.value}</MetricValue>
                  <MetricLabel>{m.label}</MetricLabel>
                  {m.explain && <Info aria-hidden='true'>ⓘ</Info>}
                  {m.onClick && <MetricArrow aria-hidden='true'>→</MetricArrow>}
                </Metric>
              ))}
            </MetricRow>
          ) : (
            subline && <Subline>{subline}</Subline>
          )}
        </Left>
        {showSplit && (
          <Splits>
            <Split>
              <SplitLabel>Task</SplitLabel>
              <SplitValue>{scoreTask === null || scoreTask === undefined ? 'No score' : `${Math.round(scoreTask)}%`}</SplitValue>
              <SplitHint>Scheduled work</SplitHint>
            </Split>
            <SplitDivider />
            <Split>
              <SplitLabel>Remedial</SplitLabel>
              <SplitValue>{scoreAction === null || scoreAction === undefined ? 'No score' : `${Math.round(scoreAction)}%`}</SplitValue>
              <SplitHint>Remedials closed</SplitHint>
            </Split>
            <SplitDivider />
            <Split>
              <SplitLabel>Risk</SplitLabel>
              <SplitValue>{scoreRisk === null || scoreRisk === undefined ? 'No score' : `${Math.round(scoreRisk)}%`}</SplitValue>
              <SplitHint>Evidence current</SplitHint>
            </Split>
          </Splits>
        )}
      </MainRow>
    </Card>
  )
}

export default ComplianceHeadline

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`

const Card = styled.section`
  position: relative;
  ${cardStyle}
  padding: 20px 22px 20px 26px;
  overflow: hidden;
  margin-bottom: 16px;
  animation: ${fadeUp} 320ms ease both;
`

const AccentRail = styled.span`
  position: absolute;
  top: 0; bottom: 0; left: 0;
  width: 5px;
  background: ${({ $accent }) => $accent};
`

const MainRow = styled.div`
  position: relative; z-index: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 24px;
  align-items: center;
  @media (max-width: ${bp.tablet}) { grid-template-columns: 1fr; }
`

const Left = styled.div` display: flex; flex-direction: column; gap: 6px; min-width: 0; `

const Eyebrow = styled.div`
  font-size: ${font.caption};
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 800;
  display: inline-flex; align-items: center; gap: 6px;
`
const ScopeChip = styled.span`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  letter-spacing: 0.08em;
`

const ScoreRow = styled.div`
  display: inline-flex; align-items: baseline; gap: 12px;
  flex-wrap: wrap;
`

const ScoreValue = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.hero};
  letter-spacing: -1.6px;
  color: ${({ $tint }) => $tint};
  line-height: 1;
  font-variant-numeric: tabular-nums;
  font-weight: 400;
`
const ScoreUnit = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.title};
  letter-spacing: -0.4px;
  color: ${({ theme }) => theme.colors.grey[800]};
  margin-left: -6px;
`

const BandChip = styled.span`
  display: inline-flex; align-items: center;
  padding: 5px 12px;
  background: ${({ $tint }) => `${$tint}1A`};
  border: 1px solid ${({ $tint }) => `${$tint}40`};
  color: ${({ $tint }) => $tint};
  border-radius: ${radius.pill};
  font-size: ${font.small};
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`

const Provisional = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 10px;
  background: #fef3c7;
  border: 1px solid rgba(161, 98, 7, 0.28);
  color: #a16207;
  border-radius: ${radius.pill};
  font-size: ${font.small};
  font-weight: 800;
  letter-spacing: 0.04em;
  cursor: help;
`

const Trend = styled.span`
  display: inline-flex; align-items: center; gap: 6px;
  font-size: ${font.body};
  font-weight: 800;
  color: ${({ $tone }) => $tone === 'up' ? '#15803d' : $tone === 'down' ? '#b00020' : ink.mute};
  font-variant-numeric: tabular-nums;
`
const TrendHint = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-size: ${font.small};
  letter-spacing: 0.04em;
`

const Subline = styled.div`
  font-size: ${font.body};
  color: ${({ theme }) => theme.colors.grey[800]};
  margin-top: 2px;
`

const TONE = {
  risk: { fg: '#b00020', bg: '#fdecea', border: 'rgba(204,34,0,0.28)' },
  warn: { fg: '#d97706', bg: '#fef3c7', border: 'rgba(161,98,7,0.28)' },
}

const MetricRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
`

const Metric = styled.div`
  font: inherit;
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  padding: 6px 11px;
  border-radius: ${radius.pill};
  border: 1px solid ${({ $tone }) => (TONE[$tone]?.border) || line.base};
  background: ${({ $tone }) => (TONE[$tone]?.bg) || surface.wash};
  color: ${({ theme }) => theme.colors.haiti};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  text-align: left;
  transition: ${transition};

  &:hover {
    ${({ $clickable }) => $clickable && 'filter: brightness(0.98);'}
  }
`

const MetricValue = styled.span`
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: ${({ $tone }) => (TONE[$tone]?.fg) || 'inherit'};
`

const MetricLabel = styled.span`
  font-size: ${font.small};
  color: ${({ theme }) => theme.colors.grey[800]};
`

const Info = styled.span`
  font-size: ${font.caption};
  color: ${ink.mute};
  cursor: help;
`

const MetricArrow = styled.span`
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary};
`

const Splits = styled.div`
  display: inline-flex; align-items: stretch;
  gap: 0;
  padding: 12px 16px;
  background: ${surface.wash};
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  @media (max-width: 480px) {
    width: 100%;
    justify-content: space-between;
    padding: 10px 8px;
  }
`
const Split = styled.div`
  display: flex; flex-direction: column; align-items: center;
  gap: 2px;
  padding: 0 16px;
  min-width: 78px;
`
const SplitLabel = styled.span`
  font-size: ${font.caption};
  font-weight: 800;
  letter-spacing: 0.10em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
`
const SplitValue = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.title};
  letter-spacing: -0.4px;
  color: ${({ theme }) => theme.colors.haiti};
  line-height: 1;
  font-variant-numeric: tabular-nums;
`
const SplitHint = styled.span`
  font-size: ${font.caption};
  color: ${({ theme }) => theme.colors.grey[800]};
  letter-spacing: 0.06em;
`
const SplitDivider = styled.span`
  width: 1px;
  background: ${line.base};
  margin: 4px 0;
`
